import Link from '@mui/material/Link';
import type { SxProps, Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';

import { mergeSx } from '@/utils/mergeSx';

import { BasicChip } from '../Chips/BasicChip';

import { useSectionFieldStyles as useStyles } from './SectionField.styles';

type Props = {
	children: ReactNode;
	to?: string | null;
	link?: string | null;
	tooltip?: string | null;
	isPrimary?: boolean;
	isSecondary?: boolean;
	customSx?: {
		root?: SxProps<Theme>;
		label?: SxProps<Theme>;
		groupName?: SxProps<Theme>;
		value?: SxProps<Theme>;
	};
};

export const SectionFieldValue = ({
	children,
	to,
	link,
	tooltip,
	isPrimary,
	isSecondary,
	customSx
}: Props) => {
	const { t } = useTranslation();
	const sx = useStyles();

	const content = (
		<>
			{children}
			{isPrimary && (
				<BasicChip
					label={t('Primary')}
					variant='primary'
					customSx={{ marginLeft: 0.5 }}
				/>
			)}
			{isSecondary && (
				<BasicChip
					label={t('Secondary')}
					variant='secondary'
					customSx={{ marginLeft: 0.5 }}
				/>
			)}
		</>
	);

	return (
		<Tooltip title={tooltip} placement='top-start'>
			<Typography
				variant='body2'
				sx={mergeSx(sx.value(!!link), customSx?.value)}
			>
				{to && (
					<Link component={RouterLink} to={to} underline='none'>
						{content}
					</Link>
				)}
				{link && (
					<Link href={link} underline='none' target='_blank'>
						{content}
					</Link>
				)}
				{!to && !link && content}
			</Typography>
		</Tooltip>
	);
};
